import { X, Target, Trophy, Goal, Zap, Crown, Medal, Clock } from 'lucide-react';
import { useWcScoring } from '@/lib/queries';

function Row({
  icon: Icon,
  title,
  desc,
  points,
  color = 'text-primary',
}: {
  icon: any;
  title: string;
  desc: string;
  points: string;
  color?: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border-light p-3">
      <span className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-bg ${color}`}>
        <Icon size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-font-primary">{title}</p>
        <p className="text-xs text-font-secondary">{desc}</p>
      </div>
      <span className="flex-shrink-0 rounded-full bg-primary-subtle px-2.5 py-1 text-xs font-bold text-primary">
        {points}
      </span>
    </div>
  );
}

export function HowPointsModal({ onClose }: { onClose: () => void }) {
  const { data: cfg } = useWcScoring();
  if (!cfg) return null;

  const mult = [
    ['Fase de grupos', cfg.multGroup],
    ['Dieciseisavos', cfg.multR32],
    ['Octavos', cfg.multR16],
    ['Cuartos', cfg.multQF],
    ['Semifinal', cfg.multSF],
    ['Final / 3er lugar', cfg.multFinal],
  ] as const;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border bg-bg-sidebar px-5 py-4 text-white">
          <div className="flex items-center gap-2">
            <Trophy size={18} className="text-primary-light" />
            <h3 className="text-base font-bold">¿Cómo se ganan los puntos?</h3>
          </div>
          <button onClick={onClose} className="rounded-full p-1 text-white/70 hover:bg-white/10 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          <section className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wide text-font-tertiary">Por cada partido</p>
            <Row
              icon={Target}
              title="Marcador exacto"
              desc="Aciertas el resultado tal cual (incluye al ganador)."
              points={`${cfg.pointsExact} pts`}
              color="text-accent"
            />
            <Row
              icon={Target}
              title="Ganador + diferencia de goles"
              desc="Aciertas quién gana y por cuántos goles, pero no el marcador exacto."
              points={`${cfg.pointsOutcomeGd} pts`}
            />
            <Row
              icon={Target}
              title="Sólo el ganador (1X2)"
              desc="Aciertas quién gana o el empate."
              points={`${cfg.pointsOutcome} pts`}
            />
            <Row
              icon={Goal}
              title="Goles exactos de un equipo"
              desc="Bonus si aciertas cuántos goles anota uno de los dos equipos."
              points={`+${cfg.pointsTeamGoals}`}
              color="text-info"
            />
            <Row
              icon={Goal}
              title="Goleador"
              desc="Bonus si el jugador que elegiste anota en el partido (dentro de los 90')."
              points={`+${cfg.pointsScorer}`}
              color="text-warning"
            />
            <div className="mt-1 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 p-2.5">
              <Clock size={15} className="mt-0.5 flex-shrink-0 text-warning" />
              <p className="text-[11px] leading-relaxed text-font-secondary">
                <span className="font-semibold text-font-primary">Se califica el marcador al minuto 90.</span>{' '}
                El tiempo extra (alargue) y los penales NO se toman en cuenta para los puntos: si un
                partido se define en tiempo extra, se califica con el resultado al final de los 90
                minutos, y el goleador sólo cuenta si anotó en ese lapso. El resultado final (con
                alargue/penales) sólo define quién avanza en el cuadro.
              </p>
            </div>
          </section>

          <section className="space-y-2">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-font-tertiary">
              <Zap size={12} /> Multiplicador por ronda
            </p>
            <p className="text-xs text-font-secondary">
              Mientras más avanza el torneo, más valen los puntos del partido.
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {mult.map(([label, m]) => (
                <div key={label} className="rounded-lg border border-border-light bg-bg p-2 text-center">
                  <p className="text-[11px] text-font-secondary">{label}</p>
                  <p className="text-lg font-extrabold text-primary">×{m}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wide text-font-tertiary">
              Pronósticos de torneo (hasta el inicio de octavos)
            </p>
            <p className="text-[11px] text-font-tertiary">
              Estos tres se otorgan al terminar el Mundial; durante el torneo no suman puntos en la tabla.
            </p>
            <Row
              icon={Crown}
              title="Campeón del Mundial"
              desc="Aciertas qué selección levanta la copa."
              points={`${cfg.pointsChampion} pts`}
              color="text-warning"
            />
            <Row
              icon={Medal}
              title="Subcampeón"
              desc="Aciertas el finalista que pierde la final."
              points={`${cfg.pointsFinalist} pts`}
            />
            <Row
              icon={Goal}
              title="Bota de Oro"
              desc="Aciertas al máximo goleador del torneo."
              points={`${cfg.pointsGoldenBoot} pts`}
              color="text-accent"
            />
          </section>

          <p className="rounded-lg bg-primary-subtle p-3 text-xs text-primary">
            Los pronósticos de cada partido se pueden editar hasta el momento del pitazo inicial.
            Después quedan bloqueados.
          </p>
        </div>
      </div>
    </div>
  );
}
